import {
    AddIcon,
    Alert,
    Button,
    Divider,
    Flex,
    Input,
    InputLabel,
    Loader,
    Text,
    TextArea,
} from "@fluentui/react-northstar";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ClipboardTask20Regular } from "@fluentui/react-icons";
import SliderTabs from "../../../components/shared/SliderTabs";
import CustomEmptyModelTask from "./CustomEmptyModelTask";
import { FromTitle } from "./style";

import { PT_CREATE_TEMPLATE } from "../constants";
import { useQuery } from "react-query";
import { api_get_tasks_of_template } from "../../../api/manage_model_tasks";
import { useFormContext } from "react-hook-form";
import { ErrorMessage } from "../../../common/styles";
import TableOfTasksForModify from "./TableOfTasksForModify";
import { useState } from "react";
import FormDialogAddManualTaskTemplate from "./FormDialogAddManualTaskTemplate";
import { useEffect } from "react";
import { I18N_NS_TRANSLATION } from "../../../constants";
import { InputStyle, WrapperAddTask } from "./DialogCreateTemplate";
import useToast from "../../../hooks/useToast";
import Toast from "../../../components/Alerts";

/* libellé + couleurs du badge par type de modèle */
const TEMPLATE_TYPES = {
    1: { label: "Onboarding", color: "#237b4b", bg: "#e7f2da" },
    2: { label: "Suivi", color: "#5b5fc7", bg: "#e8ebfa" },
    3: { label: "Offboarding", color: "#b4530f", bg: "#fdf1e7" },
};

const DialogModifyTemplate = ({
    task_template_id,
    showAlert,
    setShowAlert,
    setOpenDialogModifyTask,
    readOnly = false,
}) => {
    const { t } = useTranslation(I18N_NS_TRANSLATION);
    const [templateTasks, setTemplateTasks] = useState([]);
    const [refresh, setRefresh] = useState(null);
    const [toastConfig, setToastConfig] = useToast();

    const {
        register,
        formState: { errors },
        setValue,
        getValues,
        watch,
    } = useFormContext();

    const { data, isLoading, isFetching } = useQuery(
        ["api_get_tasks_of_template", task_template_id],
        () => api_get_tasks_of_template({ task_template_id }),
        {
            enabled: !!task_template_id && refresh === null,
            refetchOnWindowFocus: false,
            onSuccess: (data) => {
                setValue("id", data?.task_template?.id);
                setValue("name", data?.task_template?.name);
                setValue("description", data?.task_template?.description);
                setValue("tasks", data?.task_template?.task_definitions);
                setValue("type", data?.task_template?.type?.id);
                setTemplateTasks(data?.task_template?.task_definitions);
            },
        }
    );

    useEffect(() => {
        setValue("tasks", templateTasks);
    }, [setValue, templateTasks]);

    // FUNCTIONS
    const onModifyTask = (data) => {
        setToastConfig((state) => ({
            ...state,
            visible: true,
            content: "La tâche a bien été modifiée",
            level: "success",
        }));
        setOpenDialogModifyTask(data);
        return;
    };

    if (isLoading || isFetching)
        return (
            <LoaderWrapper hAlign="center" vAlign="center">
                <Loader label="Chargement ..." />
            </LoaderWrapper>
        );

    const { type, tasks, name, description } = watch();

    return (
        <>
            {showAlert?.visible && (
                <Alert
                    content={showAlert?.message}
                    visible={true}
                    success={showAlert?.level === "success" ? true : false}
                    warning={showAlert?.level === "warning" ? true : false}
                    danger={showAlert?.level === "danger" ? true : false}
                    dismissible
                    onVisibleChange={() =>
                        setShowAlert({
                            visible: false,
                            message: "",
                        })
                    }
                />
            )}
            <Toast
                {...toastConfig}
                onStatusChange={() =>
                    setToastConfig((state) => ({ ...state, visible: false }))
                }
            />
            <Flex column fill gap="gap.medium">
                {readOnly ? (
                    /* lecture seule : récapitulatif du modèle */
                    <SummaryCard column gap="gap.small">
                        <Flex gap="gap.small" vAlign="center">
                            <SummaryIcon hAlign="center" vAlign="center">
                                <ClipboardTask20Regular />
                            </SummaryIcon>
                            <Flex column fill>
                                <SummaryTitle>{name}</SummaryTitle>
                                <Flex gap="gap.small" vAlign="center">
                                    <TypePill $type={type}>
                                        {TEMPLATE_TYPES[type]?.label}
                                    </TypePill>
                                    <TaskCount>
                                        {tasks?.length ?? 0} tâche
                                        {tasks?.length > 1 ? "s" : ""}
                                    </TaskCount>
                                </Flex>
                            </Flex>
                        </Flex>
                        <SummaryDescription $empty={!description}>
                            {description || "Aucun descriptif"}
                        </SummaryDescription>
                    </SummaryCard>
                ) : (
                    <>
                        <Flex styles={{ marginTop: "10px" }}>
                            <SliderTabs
                                tabs={["Onboarding", "Suivi", "Offboarding"]}
                                onChange={(v) =>
                                    setValue("type", parseInt(v) + 1)
                                }
                                defaultActiveIndex={type - 1}
                            />
                        </Flex>
                        <Flex>
                            <TextDescription>
                                Pour ajouter un modèles de tâche, remplissez les
                                informations suivantes :
                            </TextDescription>
                        </Flex>
                        <Flex>
                            <FromTitle>Définition du modèle</FromTitle>
                        </Flex>
                        {/* forms */}
                        <Flex column gap="gap.medium">
                            <div>
                                <InputStyle
                                    {...register("name", {
                                        minLength: {
                                            value: 2,
                                            message:
                                                "Le nom doit contenir au minimum 2 caractères.",
                                        },
                                        required: {
                                            value: true,
                                            message: "Ce champ est obligatoire",
                                        },
                                    })}
                                    label="Nom du modèle"
                                    styles={{ position: "relative" }}
                                    error={errors?.name ? true : false}
                                    defaultValue={name}
                                    required
                                    clearable
                                />
                                <ErrorMessage content={errors?.name?.message} />
                            </div>
                            <Flex column>
                                <InputLabel content={"Descriptif du modèle*"} />
                                <TextArea
                                    {...register("description", {
                                        minLength: {
                                            value: 2,
                                            message:
                                                "Le descriptif doit contenir au minimum 2 caractères.",
                                        },
                                        required: {
                                            value: true,
                                            message: "Ce champ est obligatoire",
                                        },
                                    })}
                                    defaultValue={description}
                                    aria-label="description model"
                                    error={errors?.description ? true : false}
                                />
                                <ErrorMessage
                                    content={errors?.description?.message}
                                />
                            </Flex>
                        </Flex>
                    </>
                )}

                <Divider />
                <WrapperAddTask fill space="between" vAlign="center">
                    <FromTitle>Tâches comprises dans le modèle</FromTitle>
                    {/* ajouter la tâche manuelle */}
                    {!readOnly && (
                        <FormDialogAddManualTaskTemplate
                            setTemplateTasks={setTemplateTasks}
                            setToastConfig={setToastConfig}
                        />
                    )}
                </WrapperAddTask>
                <Flex fill>
                    {tasks?.length > 0 ? (
                        <TableOfTasksForModify
                            tasks={tasks}
                            setTemplateTasks={setTemplateTasks}
                            setOpenDialogModifyTask={onModifyTask}
                            setToastConfig={setToastConfig}
                            readOnly={readOnly}
                        />
                    ) : (
                        <CustomEmptyModelTask
                            title={t(
                                PT_CREATE_TEMPLATE + ".empty_illustration.title"
                            )}
                            description={t(
                                PT_CREATE_TEMPLATE +
                                    ".empty_illustration.description"
                            )}
                        />
                    )}
                </Flex>
            </Flex>
        </>
    );
};

export default DialogModifyTemplate;

const TextDescription = styled(Text)`
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: #424242;
`;

const SummaryCard = styled(Flex)`
    margin-top: 10px;
    padding: 16px;
    border: 1px solid #e8ebfa;
    border-radius: 8px;
    background: linear-gradient(180deg, #f7f8fd 0%, #ffffff 100%);
`;

const SummaryIcon = styled(Flex)`
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background-color: #e8ebfa;
    color: var(--color-primary);
`;

const SummaryTitle = styled(Text)`
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
    color: #242424;
    word-break: break-word;
`;

const TypePill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    color: ${({ $type }) => TEMPLATE_TYPES[$type]?.color ?? "#616161"};
    background-color: ${({ $type }) => TEMPLATE_TYPES[$type]?.bg ?? "#f5f5f5"};

    &::before {
        content: "";
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: currentColor;
    }
`;

const TaskCount = styled(Text)`
    font-size: 12px;
    color: #616161;
`;

const SummaryDescription = styled(Text)`
    font-size: 14px;
    line-height: 20px;
    color: ${({ $empty }) => ($empty ? "#8a8a8a" : "#424242")};
    font-style: ${({ $empty }) => ($empty ? "italic" : "normal")};
    white-space: pre-wrap;
    word-break: break-word;
`;

const LoaderWrapper = styled(Flex)`
    min-height: 200px;
`;
